import { useState, useEffect, useCallback, ReactNode } from 'react';
import { setAuthToken, typedFetch } from '@/lib/api-client';
import { AuthContext, AuthState } from './AuthContext.context';

// REQ-AUTH-001: Provider component in separate file for Fast Refresh

interface VerifyTokenResponse {
  success: boolean;
  data: {
    user: {
      id: string;
      email: string;
      createdAt: number;
    };
    sessionToken: string;
  };
}

interface LogoutResponse {
  success: boolean;
}

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [auth, setAuth] = useState<AuthState>({
    isAuthenticated: false,
    isLoading: true,
    userId: null,
    jwt: null,
    user: null,
  });

  useEffect(() => {
    console.log('[AuthContext] Initializing auth state from localStorage...');

    const jwt = localStorage.getItem('sparkry_jwt');
    const userId = localStorage.getItem('sparkry_userId');
    const userEmail = localStorage.getItem('sparkry_email');

    console.log('[AuthContext] Found in localStorage:', {
      hasJWT: !!jwt,
      hasUserId: !!userId,
      hasEmail: !!userEmail,
    });

    if (jwt && userId) {
      setAuthToken(jwt);
      setAuth({
        isAuthenticated: true,
        isLoading: false,
        userId,
        jwt,
        user: { id: userId, email: userEmail || '' },
      });
      console.log('[AuthContext] User authenticated from localStorage');
    } else {
      setAuth({
        isAuthenticated: false,
        isLoading: false,
        userId: null,
        jwt: null,
        user: null,
      });
      console.log('[AuthContext] No valid session found in localStorage');
    }
  }, []);

  const verifyToken = useCallback(async (token: string) => {
    console.log('[AuthContext] verifyToken called with token:', token.substring(0, 20) + '...');

    const response = await typedFetch<VerifyTokenResponse>('/auth/verify', {
      method: 'POST',
      body: JSON.stringify({ token }),
    });

    console.log('[AuthContext] Backend response:', JSON.stringify(response, null, 2));

    if (!response.success || !response.data) {
      console.error('[AuthContext] ERROR: Backend returned unsuccessful response');
      throw new Error('Token verification failed');
    }

    const { sessionToken } = response.data;
    const { id: userId, email } = response.data.user;

    console.log('[AuthContext] Extracted data:', { sessionToken: sessionToken.substring(0, 20) + '...', userId, email });

    if (!sessionToken || !userId) {
      console.error('[AuthContext] ERROR: Backend did not return sessionToken or userId!');
      throw new Error('Invalid response from backend - missing sessionToken or userId');
    }

    console.log('[AuthContext] Token verified successfully. Setting auth state...');

    localStorage.setItem('sparkry_jwt', sessionToken);
    localStorage.setItem('sparkry_userId', userId);
    localStorage.setItem('sparkry_email', email);
    setAuthToken(sessionToken);

    setAuth({
      isAuthenticated: true,
      isLoading: false,
      userId,
      jwt: sessionToken,
      user: { id: userId, email },
    });

    console.log('[AuthContext] Auth state updated. isAuthenticated: true');
  }, []);

  const logout = useCallback(async () => {
    console.log('[AuthContext] Logging out...');

    await typedFetch<LogoutResponse>('/auth/logout', {
      method: 'POST',
    });

    localStorage.removeItem('sparkry_jwt');
    localStorage.removeItem('sparkry_userId');
    localStorage.removeItem('sparkry_email');
    setAuthToken(null);
    setAuth({
      isAuthenticated: false,
      isLoading: false,
      userId: null,
      jwt: null,
      user: null,
    });

    console.log('[AuthContext] Logged out successfully');
  }, []);

  return <AuthContext.Provider value={{ auth, verifyToken, logout }}>{children}</AuthContext.Provider>;
}
